"use client";

import React, { Suspense, useState, useEffect, useCallback, useMemo } from "react";
import dynamic from "next/dynamic";
import FormatSiteNavigation from "@/components/client/SiteNavigation/FormatSiteNavigation";
import TrimbleDeveloperHeader from "@/components/client/TrimbleDeveloperNavigation/TrimbleDeveloperHeader";
import type { TDNavigationData } from "@/lib/formatters/formatTDNavigation";
import useLanguage from "@/lib/useLanguage";
import useFlyout from "@/lib/useFlyout";
import { usePathname } from "next/navigation";
import getConfigTID from "@/lib/getConfigTID";
import { env, tidBaseUrl } from "@/lib/config/environments";
import Analytics from "@/components/client/Analytics";
import useAuthTID from "@/lib/useAuthTID";
import { useEcomStore } from "@/context/ecom/EcomContext";
import { useLogin } from "@/context/login/LoginContext";
import { isUserAuthenticated } from "@/context/login/reducer";
import SkipToContent from "@/components/client/SkipToContent/SkipToContent";
import { clearKnownUser } from "@/lib/knownUserCookie";
import { ExperienceConfiguration } from "@ninetailed/experience.js-next";
import NinetailedGoogleTagmanagerPlugin from "@ninetailed/experience.js-plugin-google-tagmanager";
import NinetailedInsightsPlugin from "@ninetailed/experience.js-plugin-insights";
import type NinetailedPreviewPluginType from "@ninetailed/experience.js-plugin-preview";
import { NinetailedProvider } from "@ninetailed/experience.js-react";
import { NinetailedPrivacyPlugin } from "@ninetailed/experience.js-plugin-privacy";
import NinetailedConsentSync from "@/components/client/Personalization/NinetailedConsentSync";
import { ConsentProvider } from "@/components/client/Consent/ConsentProvider";
import AlgoliaInsightsBootstrap from "@/components/client/Consent/AlgoliaInsightsBootstrap";
import SsoParamsCleaner from "@/components/client/SsoParamsCleaner";
import UtmLinkDecorator from "@/components/client/UtmLinkDecorator";
import NavLoading from "@/components/client/SiteNavigation/loading";
import AlertBanners from "@/components/client/AlertBanner/AlertBanners";
import type { FormattedAlertBanner } from "@/components/formatters/MXPAlertBanner";
import lightTexture from "@/components/terra/images/light-texture-tile.webp";
import { emitGTMEvent, syncStoredUserInfo } from "@/components/client/Datalayer/utilities";
import useFlyoutCart from "@/lib/FlyoutCart/useFlyoutCart";
import useAuthCheck from "@/lib/hooks/useAuthCheck";
import useScrollLock from "@/lib/useScrollLock";
// import LivePreviewProvider from "@/components/client/Providers/LivePreviewProvider"

// Cart/currency/account error dialogs are closed by default on every page
// load. Code-splitting + client-only mounting keeps their markup and the
// `trimble-terra` Dialog component out of the server-rendered DOM and the
// main entry chunk until FlyoutCart actually needs to show one.
const Dialogs = dynamic(() => import("@/lib/FlyoutCart/Dialogs"), {
  ssr: false
});
const RouteTracker = dynamic(() => import("@/components/client/RouteTracker/RouteTracker").then(mod => ({
  default: mod.RouteTracker
})), {
  ssr: false
});
const TrackPage = dynamic(() => import("@/components/client/Personalization/TrackPage"), {
  ssr: false
});
const NavigationProgress = dynamic(() => import("@/components/client/NavigationProgress"), {
  ssr: false
});
type Audience = {
  id: string;
  name: string;
  description?: string | undefined;
};

// Module-level singleton for the Ninetailed preview plugin.
//
// The plugin's initialize() appends a fixed-position div + zoid iframe
// directly to document.body. Its only guard against duplicate attaches
// is a TOCTOU check against the DOM ("is a .nt-preview-widget-container
// element already present?") at the start of an async initialize. In
// dev (`reactStrictMode: true` + Next.js HMR + Suspense remounts) the
// LayoutComponent effect can fire several times before any in-flight
// initialize has appended its div, so every call sails past that guard
// and produces a duplicate container. We've reproduced this with four
// containers stacked at right:0; bottom:128px — the visible iframe is
// then whichever one is topmost in DOM order, while window.ninetailed
// .plugins.preview points to whichever wrote last, and the bridge.update
// Props calls also race. Clicks become unpredictable / no-ops.
//
// Solution: cache the plugin promise at module scope so every effect
// run resolves to the *same* plugin instance, and monkey-patch its
// initialize() to be idempotent so the second analytics instance that
// NinetailedProvider creates on remount doesn't flip isActiveInstance
// to false (which would silently break open/close/toggle on the still-
// attached widget).
let cachedPreviewPluginPromise: Promise<any> | null = null;
function getPreviewPlugin(PluginCtor: typeof NinetailedPreviewPluginType, options: ConstructorParameters<typeof NinetailedPreviewPluginType>[0]): Promise<any> {
  if (cachedPreviewPluginPromise) return cachedPreviewPluginPromise;
  cachedPreviewPluginPromise = Promise.resolve().then(() => {
    const instance: any = new PluginCtor(options);
    const originalInitialize = instance.initialize.bind(instance);
    let didInit = false;
    instance.initialize = async (...args: unknown[]) => {
      if (didInit) return;
      didInit = true;
      return originalInitialize(...args);
    };
    return instance;
  });
  return cachedPreviewPluginPromise;
}

// IMPORTANT: NinetailedProvider builds its SDK instance once
// (useMemo with [] deps) and never re-reads `plugins`, so every plugin
// must be present on the provider's FIRST render. Swapping plugins in
// after mount is silently ignored — that previously dropped Insights
// (no component view/experiment analytics in Contentful) and Privacy
// (no consent gating, no window.ninetailed.consent). Cached at module
// scope so remounts (StrictMode/HMR/route changes) reuse the same
// instances.
let cachedBasePlugins: any[] | null = null;
function getBasePlugins(): any[] {
  if (cachedBasePlugins) return cachedBasePlugins;
  cachedBasePlugins = [new NinetailedInsightsPlugin(), new NinetailedGoogleTagmanagerPlugin(), new NinetailedPrivacyPlugin({
    allowedEvents: []
  })];
  return cachedBasePlugins;
}
const LayoutComponent = ({
  profile,
  navData,
  tdNavData,
  flyoutData,
  flyoutCartData,
  ecommerceSettings,
  lang,
  config,
  children,
  siteLangs,
  displayActionBlock = false,
  ninetailed,
  ninetailedPreviewEnabled = false,
  rawAppData,
  isStrictLocale = false,
  alertBanner = null
}: {
  profile: any;
  navData: any;
  tdNavData?: TDNavigationData | null;
  lang: string;
  config: any;
  children: React.ReactNode;
  siteLangs: any[];
  displayActionBlock?: boolean;
  flyoutData: any;
  ninetailed?: {
    allExperiences: ExperienceConfiguration[];
    allAudiences: Audience[];
    clientId: string;
    environment: string;
  };
  ninetailedPreviewEnabled?: boolean;
  flyoutCartData: any;
  ecommerceSettings: any;
  rawAppData?: any;
  isStrictLocale?: boolean;
  alertBanner?: FormattedAlertBanner | null;
}) => {
  const siteNavPack = {
    data: navData,
    profile: profile,
    config: config,
    appData: rawAppData
  };
  const [state] = useEcomStore() ?? [];
  const loginCtx = useLogin();
  const [loginState, loginDispatch] = loginCtx ?? [];
  const [isSectorFlyoutDeployed, setSectorFlyoutDeployed] = useState(false);
  const isAuthenticated = isUserAuthenticated(loginState);

  // Padding is driven by a CSS variable (--nav-height) defined in globals.css
  // with responsive defaults so the initial render never shifts.  The nav's
  // ResizeObserver calls this to override when the measured height differs.
  const setPageTopPadding = useCallback((height: number) => {
    document.getElementById("page-content")?.style.setProperty("--nav-height", `${height}px`);
  }, []);
  const handleSectorFlyoutDeployed = (isFlyoutDeployed?: boolean) => {
    setSectorFlyoutDeployed(typeof isFlyoutDeployed === "undefined" ? !isSectorFlyoutDeployed : isFlyoutDeployed);
  };
  const routePathname = usePathname();
  const isProductBuyPage = /\/products\/[^/]+\/buy\/?$/.test(routePathname ?? "");
  const isTrimbleDeveloperSite = profile?.NEXT_PUBLIC_SITE_MACHINE_NAME === "trimble-developer";
  const [origin, setOrigin] = useState("");
  useEffect(() => {
    setOrigin(`${window.location.protocol}//${window.location.host}`);
  }, []);
  const path = origin ? `${origin}${routePathname}` : "";
  const tidConfig = useMemo(() => getConfigTID(), []);
  const {
    queryString,
    redirect_uri,
    code_verifier,
    client_id,
    url,
    tokenEndpoint,
    signUpQuery,
    env
  } = tidConfig;

  // TODO: needs to be wrapped in suspense
  useAuthTID({
    path,
    redirect_uri,
    code_verifier,
    client_id: client_id ?? "",
    url,
    tokenEndpoint
  });
  const isLivePreview = profile?.NEXT_PUBLIC_MODE === "live-preview";

  // Trimble Developer pages need silent-SSO login persistence regardless of
  // the sitewide nav's CMS-configured `disableLogin` field, since that field
  // is meant to control the main site, not the TD header. Rather than having
  // TrimbleDeveloperHeader run its *own* competing useAuthCheck() (which
  // previously raced this call over the same shared isAuthCheckComplete flag
  // and caused redirect-loop edge cases), this is the single source of truth
  // for the check on every route, TD included.
  useAuthCheck({
    disableLogin: (navData?.disableLogin || isLivePreview) && !isTrimbleDeveloperSite
  });
  const handleLogout = () => {
    const logoutBaseUrl = url.replace("/oauth/token", "/oauth");
    const tidRedirect_uri = env === "prod" ? `https://mxp-api.trimble.com/tid/prod/redirect` : `https://mxp-api-stage.netlify.app/tid/${env}/redirect`;
    const tidStateUrl = new URL(path || `${origin}${routePathname}`);
    const tidState = tidStateUrl.toString();
    const idTokenHint = loginState?.idToken ?? "";
    emitGTMEvent({
      event: "login",
      method: "tid",
      login_status: "logout",
      account_status: null
    });
    syncStoredUserInfo(null);
    loginDispatch?.({
      type: "LOG_OUT"
    });
    clearKnownUser();
    window.open(`${logoutBaseUrl}/logout?id_token_hint=${idTokenHint}&post_logout_redirect_uri=${tidRedirect_uri}&state=${tidState}`, "_self");
  };
  const sectorFlyoutData = useFlyout(flyoutData, navData?.disableLogin || isLivePreview, lang, isAuthenticated, loginState?.user ?? null, handleLogout, path, queryString, signUpQuery, ecommerceSettings, redirect_uri, code_verifier, client_id, url);
  const isCartFlyoutDisabled = process.env.NEXT_PUBLIC_ECOMM_ENABLED !== "true" ? true : navData?.disableCartFlyout;
  const flyoutCartConfig = useFlyoutCart({
    disableCartFlyout: isCartFlyoutDisabled,
    isAuthenticated,
    rawFlyoutCartData: flyoutCartData ?? {},
    ecommerceSettings,
    queryString,
    path,
    client_id,
    redirect_uri,
    code_verifier
  });
  useScrollLock(!!flyoutCartConfig?.isCartFlyoutOpen || !!flyoutCartConfig?.isSubscriptionError);
  useLanguage(lang, siteLangs, loginState?.user?.tid, client_id, redirect_uri, code_verifier, url);

  // Insights/GTM/Privacy are available synchronously so the provider mounts
  // with them on first render. The preview plugin is dynamically imported to
  // keep it out of production bundles; when it IS enabled (dev/staging only)
  // the provider waits for it, since plugins can't be added after mount.
  const [providerPlugins, setProviderPlugins] = useState<any[] | null>(() => ninetailedPreviewEnabled ? null : getBasePlugins());
  useEffect(() => {
    if (!ninetailedPreviewEnabled) return;
    let cancelled = false;
    const basePlugins = getBasePlugins();
    const spaceId = process.env.NEXT_PUBLIC_CF_MXP_SPACE_ID;
    const envId = process.env.NEXT_PUBLIC_CF_MXP_ENVIRONMENT || "master";
    const openContentfulEntry = (entryId: string) => {
      if (!spaceId) return;
      window.open(`https://app.contentful.com/spaces/${spaceId}/environments/${envId}/entries/${entryId}`, "_blank");
    };
    import("@ninetailed/experience.js-plugin-preview").then(async mod => {
      const NinetailedPreviewPlugin = mod.default as typeof NinetailedPreviewPluginType;
      // Always resolve to the same singleton plugin instance regardless
      // of how many times this effect fires (StrictMode, HMR, Suspense).
      const plugin = await getPreviewPlugin(NinetailedPreviewPlugin, {
        experiences: ninetailed?.allExperiences || [],
        audiences: ninetailed?.allAudiences || [],
        onOpenExperienceEditor: experience => openContentfulEntry(experience.id),
        onOpenAudienceEditor: audience => openContentfulEntry(audience.id)
      });
      if (cancelled) return;
      setProviderPlugins([...basePlugins, plugin]);
    }).catch(err => {
      console.error("[Ninetailed] preview plugin failed to load; rendering without it", err);
      if (!cancelled) setProviderPlugins(basePlugins);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (providerPlugins === null) return <section className="min-h-screen" />;
  return <>
      <Suspense fallback={null}>
        <NavigationProgress />
      </Suspense>
      <Suspense fallback={null}>
        <SsoParamsCleaner />
      </Suspense>
      <UtmLinkDecorator />
      <ConsentProvider oneTrustId={profile?.NEXT_PUBLIC_ONETRUST_ID} isDevelopment={profile?.NEXT_PUBLIC_DEPLOYMENT_ENV === "development"} bypassConsentCheck={!!profile?.BYPASS_CONSENT_CHECK || !!process.env.NEXT_PUBLIC_BYPASS_CONSENT_CHECK}>
        <Analytics profile={profile} />
        <AlgoliaInsightsBootstrap appId={profile?.NEXT_PUBLIC_ALGOLIA_APP_ID} apiKey={profile?.NEXT_PUBLIC_ALGOLIA_API_KEY} oneTrustId={profile?.NEXT_PUBLIC_ONETRUST_ID} consentCategoryId={profile?.NEXT_PUBLIC_ALGOLIA_CONSENT_CATEGORY} isDevelopment={profile?.NEXT_PUBLIC_DEPLOYMENT_ENV === "development"} bypassConsentCheck={!!profile?.BYPASS_CONSENT_CHECK || !!process.env.NEXT_PUBLIC_BYPASS_CONSENT_CHECK}>
          <NinetailedProvider clientId={ninetailed?.clientId || ""} environment={ninetailed?.environment || ""} plugins={providerPlugins}>
            <TrackPage />
            <NinetailedConsentSync />
            {!isTrimbleDeveloperSite && <SkipToContent />}
            <section className="min-h-screen">
              {isTrimbleDeveloperSite && tdNavData && <TrimbleDeveloperHeader logoHref={tdNavData.logoHref} navLinks={tdNavData.navLinks} helpLinks={tdNavData.helpLinks} showAssistant={tdNavData.showAssistant} showThemeToggle={tdNavData.showThemeToggle} showNotifications={tdNavData.showNotifications} signInLink={tdNavData.signInLink} />}
              <AlertBanners slot="alert" banner={alertBanner} />
              {profile?.NEXT_PUBLIC_RENDER_NAV && config !== undefined && config.siteNavigation !== null && !isTrimbleDeveloperSite && <Suspense fallback={<NavLoading />}>
                    <FormatSiteNavigation {...siteNavPack} siteLangs={siteLangs} handleSectorFlyoutDeployed={handleSectorFlyoutDeployed} isSectorFlyoutDeployed={isSectorFlyoutDeployed} sectorFlyoutData={sectorFlyoutData} isAuthenticated={isAuthenticated} rawAppData={rawAppData} isStrictLocale={isStrictLocale} flyoutCart={{
                isDisabled: isCartFlyoutDisabled,
                cartData: state?.cart,
                cartSettings: flyoutCartConfig?.cartSettings || {},
                onCartRemoveItem: flyoutCartConfig?.handleCartRemoveItem,
                onCartChangeQuantity: flyoutCartConfig?.handleCartChangeQuantity,
                isCartFlyoutOpen: flyoutCartConfig?.isCartFlyoutOpen,
                onCartFlyoutOpen: flyoutCartConfig?.setIsCartFlyoutOpen,
                currencies: flyoutCartConfig?.currencies,
                defaultCurrency: flyoutCartConfig?.defaultCurrency,
                activeCurrency: flyoutCartConfig?.currency,
                setCurrency: flyoutCartConfig?.setCurrency,
                isCurrencyLocked: flyoutCartConfig?.isCurrencyLocked
              }} setPageTopPadding={setPageTopPadding} />
                  </Suspense>}
              <main id="page-content" style={isProductBuyPage ? {
              backgroundColor: "#ffffff"
            } : {
              backgroundImage: `url(${lightTexture.src})`,
              backgroundRepeat: "repeat",
              backgroundSize: "auto"
            }}>
                <RouteTracker />
                {children}
              </main>
              {displayActionBlock && <div className="action-block-spacer"></div>}
            </section>
            {!isCartFlyoutDisabled && <Dialogs isServerError={flyoutCartConfig?.isServerError} setServerError={flyoutCartConfig?.setServerError} isSubscriptionError={flyoutCartConfig?.isSubscriptionError} setSubscriptionError={flyoutCartConfig?.setSubscriptionError} productsRemoved={flyoutCartConfig?.productsRemoved} setProductsRemoved={flyoutCartConfig?.setProductsRemoved} isCartUpdatedByAPI={flyoutCartConfig?.isCartUpdatedByAPI} setCartUpdatedByAPI={flyoutCartConfig?.setCartUpdatedByAPI} multiErrorMessage={flyoutCartConfig?.multiErrorMessage} setMultiErrorMessage={flyoutCartConfig?.setMultiErrorMessage} isAccountError={flyoutCartConfig?.isAccountError} setIsAccountError={flyoutCartConfig?.setIsAccountError} ecommerceSettings={ecommerceSettings} onClose={flyoutCartConfig?.handleCloseDialogs} />}
          </NinetailedProvider>
        </AlgoliaInsightsBootstrap>
      </ConsentProvider>
    </>;
};
export default LayoutComponent;